import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { berlinDate, diffDays, DOCUMENT_CATEGORY_LABELS, EMPLOYEE_FILE_KIND_LABELS, EMPLOYEE_FILE_KINDS, type DocumentItem, type EmployeeFile, type EmployeeFileKind, type Paginated } from '@kontor/shared';
import { api, ApiError, apiUpload, queryString } from '../../api';
import { useSession } from '../../session';
import { CollapsibleCard } from '../../components/CollapsibleCard';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
/** Zustand eines befristeten Vertrags: abgelaufen, läuft bald ab (30 Tage) oder in Ordnung */
function expiry(validUntil: string | null): { text: string; tone: 'expired' | 'soon' } | null {
  if (!validUntil) return null;
  const days = diffDays(berlinDate(), validUntil);
  if (days < 0) return { text: 'abgelaufen', tone: 'expired' };
  if (days <= 30) return { text: days === 0 ? 'läuft heute ab' : days === 1 ? 'läuft morgen ab' : `läuft in ${days} Tagen ab`, tone: 'soon' };
  return null;
}
const day = (d: string) => dateFormat.format(new Date(`${d}T12:00:00`));
const size = (bytes: number) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`);

/**
 * Dokumente aus dem Modul Dokumente, die zur Karte des Mitarbeiters gehören. Dort legt Wirkia zum Beispiel einen digital e-signierten Vertrag ab. Ohne das Modul oder das Leserecht
 * bleibt die Liste leer.
 */
export function useEmployeeDocuments(employeeId: string): DocumentItem[] | undefined {
  const { can, hasModule } = useSession();
  const enabled = hasModule('documents') && can('documents.read');
  const docs = useQuery({
    queryKey: ['documents', { employeeId }],
    queryFn: async () => (await api<Paginated<DocumentItem>>('GET', `/documents${queryString({ employeeId, perPage: 100, sort: '-createdAt' })}`)).data,
    enabled,
  });
  return enabled ? docs.data : [];
}

/** Verträge und andere Dateien zum Mitarbeiter (KON-077). Hochladen mit dem Schreibrecht, Entfernen nur der Inhaber. */
export function EmployeeFiles({ employeeId, onChanged }: { employeeId: string; onChanged: () => void }) {
  const { can, session } = useSession();
  const client = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<EmployeeFileKind>('contract');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const isOwner = session?.user.role.key === 'owner';

  const files = useQuery({ queryKey: ['employee-files', employeeId], queryFn: async () => (await api<{ data: EmployeeFile[] }>('GET', `/employees/${employeeId}/files`)).data });
  const refresh = () => {
    client.invalidateQueries({ queryKey: ['employee-files', employeeId] });
    onChanged();
  };
  const upload = useMutation({
    mutationFn: (file: File) => apiUpload<{ data: EmployeeFile }>(`/employees/${employeeId}/files`, file, { 'X-File-Kind': kind, ...(validFrom ? { 'X-Valid-From': validFrom } : {}), ...(validUntil ? { 'X-Valid-Until': validUntil } : {}) }),
    onSuccess: () => {
      setValidFrom('');
      setValidUntil('');
      if (input.current) input.current.value = '';
      refresh();
    },
  });
  const remove = useMutation({ mutationFn: (id: string) => api('DELETE', `/employees/${employeeId}/files/${id}`), onSuccess: refresh });

  const error = [upload.error, remove.error, files.error].find((e) => e instanceof ApiError) as ApiError | undefined;
  const list = files.data ?? [];
  const stored = useEmployeeDocuments(employeeId) ?? [];

  return (
    <CollapsibleCard title="Dokumente" storageKey="employee-files">
      {error && <div className="alert alert--error" role="alert">{error.message}</div>}
      {files.isLoading && <p className="muted">Wird geladen …</p>}
      {!files.isLoading && list.length === 0 && stored.length === 0 && <p className="muted">Noch keine Datei abgelegt.</p>}
      <ul className="file-list">
        {list.map((f) => (
          <li key={f.id}>
            <div>
              <a href={`/api/v1/employees/${employeeId}/files/${f.id}/download`} download={f.fileName} className="strong-link" onClick={() => window.setTimeout(onChanged, 800)}>{f.fileName}</a>
              <div className="muted small">
                {EMPLOYEE_FILE_KIND_LABELS[f.kind]}
                {f.validFrom && f.validUntil ? ` · gültig ${day(f.validFrom)} bis ${day(f.validUntil)}` : f.validFrom ? ` · gültig ab ${day(f.validFrom)}` : f.validUntil ? ` · gültig bis ${day(f.validUntil)}` : ''} · {size(f.sizeBytes)} · hochgeladen {dateFormat.format(new Date(f.createdAt))}
                {f.uploadedByName ? ` von ${f.uploadedByName}` : ''}
                {expiry(f.validUntil) && <span className={`badge file-expiry file-expiry--${expiry(f.validUntil)!.tone}`}>{expiry(f.validUntil)!.text}</span>}
              </div>
            </div>
            {isOwner && (
              <button
                type="button"
                className="btn btn--danger btn--sm"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Datei „${f.fileName}“ endgültig entfernen? Das lässt sich nicht rückgängig machen.`)) remove.mutate(f.id);
                }}
              >
                Entfernen
              </button>
            )}
          </li>
        ))}
        {stored.map((d) => (
          <li key={d.id}>
            <div>
              <a href={`/api/v1/documents/${d.id}/download`} download={d.fileName} className="strong-link">{d.title ?? d.fileName}</a>
              <div className="muted small">
                {DOCUMENT_CATEGORY_LABELS[d.category]} · {size(d.sizeBytes)} · abgelegt {dateFormat.format(new Date(d.createdAt))}
                {d.category === 'contract' && /e-signiert|unterschrieben/i.test(d.note ?? '') && <span className="badge badge--contract-signed">Digital E-signiert</span>}
              </div>
              {d.note && <div className="muted small">{d.note}</div>}
            </div>
          </li>
        ))}
      </ul>

      {can('hr.write') && (
        <div className="file-upload">
          <label className="field">
            <span>Art</span>
            <select className="input" value={kind} onChange={(e) => setKind(e.target.value as EmployeeFileKind)}>
              {EMPLOYEE_FILE_KINDS.map((k) => <option key={k} value={k}>{EMPLOYEE_FILE_KIND_LABELS[k]}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Gültig ab (freiwillig)</span>
            <input className="input" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          </label>
          <label className="field">
            <span>Gültig bis (bei Befristung)</span>
            <input className="input" type="date" value={validUntil} min={validFrom || undefined} onChange={(e) => setValidUntil(e.target.value)} />
          </label>
          <label className="field">
            <span>Datei (PDF, JPG oder PNG, bis 5 MB)</span>
            <input ref={input} className="input" type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic" disabled={upload.isPending} onChange={(e) => e.target.files?.[0] && upload.mutate(e.target.files[0])} />
          </label>
          {upload.isPending && <p className="muted small">Wird hochgeladen …</p>}
        </div>
      )}
    </CollapsibleCard>
  );
}
